/**
 * TrendsParts.tsx — the Phase 2 trends areas of the System Health page.
 *
 * Contract: services/backoffice/app/contracts_trends.py. Design:
 * Architecture/20261104_FOWLER_system-health-redesign-design.md. Every part degrades
 * on its own: a missing part reads "Unavailable" (with the backend's reason from
 * `unavailable[]`, matched by exact part name), "Warming up" or "Stale since <time>"
 * in text — never a blank, never an invented number.
 */

import { useTranslation } from 'react-i18next';
import type {
  HealthItem,
  HealthTrends,
  MetaSends,
  TimelineEvent,
  WakeupStats,
} from '../../api/types';

/** Past this age the served trends are flagged stale (twice the backend's 300 s cache). */
export const TRENDS_STALE_AFTER_SECONDS = 600;
/** Cost lags 8 to 24 h by design; beyond this many days the figure is flagged stale. */
const COST_STALE_AFTER_DAYS = 2;
const TIMELINE_DAYS = 7;
const BAR_MAX_PX = 28;
const BAR_MIN_PX = 4;

/** What the page knows about trends: not yet fetched, fetched, or last fetch failed. */
export interface TrendsView {
  loaded: boolean;
  data: HealthTrends | null;
  /** The latest fetch failed; `data`, if any, is the last good value. */
  failed: boolean;
}

export const INITIAL_TRENDS_VIEW: TrendsView = { loaded: false, data: null, failed: false };

export interface FieldValue {
  label: string;
  text: string;
  muted: boolean;
}

export const useTrendsText = (view: TrendsView) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const nf = (o: Intl.NumberFormatOptions) => new Intl.NumberFormat(lang, o);
  const whole = (n: number) => nf({ maximumFractionDigits: 0 }).format(n);
  const pct = (fraction: number, min: number, max: number) =>
    nf({ style: 'percent', minimumFractionDigits: min, maximumFractionDigits: max }).format(
      fraction,
    );
  const secs = (n: number) => t('health.trends.seconds', { value: whole(n) });
  const ms = (n: number) =>
    n >= 1000
      ? t('health.trends.seconds', { value: nf({ maximumFractionDigits: 1 }).format(n / 1000) })
      : t('health.trends.millis', { value: whole(n) });
  const when = (iso: string) =>
    new Intl.DateTimeFormat(lang, { dateStyle: 'short', timeStyle: 'short' }).format(new Date(iso));

  /** The backend's reason when one of `parts` (exact names) is listed as unavailable. */
  const reasonFor = (...parts: string[]): string | undefined =>
    view.data?.unavailable.find((u) => parts.includes(u.part))?.reason;

  /** Text for a part with no value. "trends" is the whole-payload failure and always counts. */
  const absent = (...parts: string[]): string => {
    if (!view.loaded) return t('health.trends.loading');
    if (!view.data) return t('health.trends.unavailable');
    if (view.data.status === 'warming') return t('health.trends.warming');
    const reason = reasonFor('trends', 'inventory', ...parts);
    return reason ? t('health.trends.unavailableWhy', { reason }) : t('health.trends.unavailable');
  };

  const { data } = view;
  const staleSince =
    data?.generated_at && (view.failed || (data.age_seconds ?? 0) > TRENDS_STALE_AFTER_SECONDS)
      ? data.generated_at
      : null;

  return { t, lang, whole, pct, secs, ms, when, reasonFor, absent, staleSince };
};

export const StaleNote = ({ view }: { view: TrendsView }) => {
  const x = useTrendsText(view);
  if (!x.staleSince) return null;
  return (
    <span data-testid="trends-stale" className="mt-1 block text-meta text-status-degraded">
      {x.t('health.trends.staleSince', { when: x.when(x.staleSince) })}
    </span>
  );
};

/** Header line: how old the trends are, or why there are none. */
export const TrendsAge = ({ view }: { view: TrendsView }) => {
  const x = useTrendsText(view);
  let text: string;
  if (!view.data) text = x.absent();
  else if (view.data.status === 'warming') text = x.t('health.trends.warming');
  else if (view.data.age_seconds === null) text = x.absent();
  else {
    const rtf = new Intl.RelativeTimeFormat(x.lang, { numeric: 'auto', style: 'short' });
    const s = Math.max(0, Math.floor(view.data.age_seconds));
    text = s < 60 ? rtf.format(-s, 'second') : rtf.format(-Math.floor(s / 60), 'minute');
  }
  return (
    <span data-testid="trends-age" className="text-meta text-ink-3">
      {x.t('health.trends.label')}: {text}
    </span>
  );
};

const Muted = ({ children }: { children: string }) => (
  <span className="text-meta text-ink-3">{children}</span>
);

const WakeBars = ({ stats, view }: { stats: WakeupStats; view: TrendsView }) => {
  const x = useTrendsText(view);
  const { recent } = stats;
  if (recent.length === 0) return null;
  const durations = recent.flatMap((r) => (r.duration_s === null ? [] : [r.duration_s]));
  const longest = Math.max(...durations, 1);
  const base = { count: recent.length, ok: recent.filter((r) => r.ok).length };
  const label =
    durations.length > 0
      ? x.t('health.trends.wakeBars', { ...base, worst: x.whole(longest) })
      : x.t('health.trends.wakeBarsNoDuration', base);
  return (
    <div
      role="img"
      aria-label={label}
      data-testid="wake-bars"
      className="mt-1 flex h-7 items-end gap-[3px]"
    >
      {recent.map((r) => (
        <i
          key={r.at}
          data-ok={r.ok}
          // Failed wake-ups are hollow and outlined, successful ones solid: not colour alone.
          className={`w-2 rounded-[1px] ${r.ok ? 'bg-ink-3' : 'border-2 border-status-down bg-transparent'}`}
          style={{
            height:
              r.duration_s === null
                ? BAR_MIN_PX
                : Math.max(BAR_MIN_PX, (r.duration_s / longest) * BAR_MAX_PX),
          }}
        />
      ))}
    </div>
  );
};

const Wakeups = ({ stats, view }: { stats: WakeupStats; view: TrendsView }) => {
  const x = useTrendsText(view);
  if (stats.total_7d === 0 && stats.recent.length === 0)
    return <Muted>{x.t('health.trends.noWakeups')}</Muted>;
  // Newest wake-up still pending: the backend nulls last_*; counts and bars still include it.
  const pending = stats.last_ok === null && stats.last_at === null;
  const result =
    stats.last_ok === null ? null : x.t(`health.trends.wake.${stats.last_ok ? 'ok' : 'failed'}`);
  const last =
    stats.last_duration_s !== null && result
      ? `${x.secs(stats.last_duration_s)}, ${result}`
      : (result ?? x.t('health.fields.notAvailable'));
  return (
    <>
      <div className="text-h2 text-ink-1">
        {pending ? x.t('health.trends.wakePending') : last}
        {stats.last_at ? (
          <span className="ml-2 text-meta text-ink-3">
            {x.t('health.trends.lastWake', { when: x.when(stats.last_at) })}
          </span>
        ) : null}
      </div>
      <div className="text-meta text-ink-3">
        {x.t('health.trends.wakeSummary', { ok: stats.ok_7d, total: stats.total_7d })}
        {stats.median_s !== null && stats.worst_s !== null
          ? ` · ${x.t('health.trends.wakeSpread', { median: x.secs(stats.median_s), worst: x.secs(stats.worst_s) })}`
          : ''}
      </div>
      <WakeBars stats={stats} view={view} />
    </>
  );
};

const Availability = ({
  view,
  a24,
  a7,
}: {
  view: TrendsView;
  a24: number | null;
  a7: number | null;
}) => {
  const x = useTrendsText(view);
  const cell = (label: string, v: number | null) => (
    <span className="mr-3 inline-block">
      <span className="mr-1 text-xs uppercase text-ink-3">{label}</span>
      <span className="text-h2 text-ink-1">
        {v === null ? x.t('health.fields.notAvailable') : x.pct(v, 0, 2)}
      </span>
    </span>
  );
  return (
    <div data-testid="availability">
      {cell(x.t('health.trends.window24h'), a24)}
      {cell(x.t('health.trends.window7d'), a7)}
    </div>
  );
};

/** Headline cell of a container app: availability if always on, wake-ups if it sleeps. */
export const AppHeadline = ({
  item,
  view,
  fallback,
}: {
  item: HealthItem;
  view: TrendsView;
  fallback: string;
}) => {
  const x = useTrendsText(view);
  const group = item.detail?.group;
  if (group !== 'always_on' && group !== 'sleeps_when_idle')
    return <span className="break-words text-h2 text-ink-1">{fallback}</span>;
  const ct = view.data?.components[item.key];
  let body;
  if (group === 'always_on') {
    body =
      ct && (ct.availability_24h !== null || ct.availability_7d !== null) ? (
        <Availability view={view} a24={ct.availability_24h} a7={ct.availability_7d} />
      ) : (
        <Muted>{x.absent(`replicas:${item.key}`, `requests:${item.key}`)}</Muted>
      );
  } else if (ct?.wakeups) {
    body = <Wakeups stats={ct.wakeups} view={view} />;
  } else if (ct && !x.reasonFor('wakeups', 'trends')) {
    // The app is in trends and "wakeups" is not listed as failed: it simply never woke.
    body = <Muted>{x.t('health.trends.noWakeups')}</Muted>;
  } else {
    body = <Muted>{x.absent('wakeups')}</Muted>;
  }
  return (
    <>
      {body}
      <StaleNote view={view} />
    </>
  );
};

/** Reliability fields of a container app that come from trends. */
export const useAppTrendFields = (item: HealthItem, view: TrendsView): FieldValue[] => {
  const x = useTrendsText(view);
  const ct = view.data?.components[item.key];
  const replicas = item.detail?.replicas;
  const k = item.key;
  const f = (label: string, text: string, muted = false): FieldValue => ({ label, text, muted });
  const gone = (label: string, part: string) => f(label, x.absent(part), true);
  const errLabel = x.t('health.trends.errorRate');
  const avgLabel = x.t('health.trends.avgResponse');

  let errorRate = gone(errLabel, `requests:${k}`);
  if (ct && x.reasonFor(`requests:${k}`, 'trends')) errorRate = gone(errLabel, `requests:${k}`);
  else if (ct && ct.error_rate_24h !== null)
    errorRate = f(errLabel, x.pct(ct.error_rate_24h, 1, 1));
  else if (ct && ct.requests_24h === 0)
    errorRate = f(errLabel, x.t('health.trends.noRequests'), true);

  let avg = gone(avgLabel, `response:${k}`);
  if (ct && x.reasonFor(`response:${k}`, 'trends')) avg = gone(avgLabel, `response:${k}`);
  else if (ct && ct.avg_response_ms_24h !== null) avg = f(avgLabel, x.ms(ct.avg_response_ms_24h));
  else if (ct && ct.requests_24h !== null)
    avg = f(avgLabel, x.t('health.trends.tooFewRequests'), true);

  const gauge = (label: string, v: number | null | undefined, part: string): FieldValue => {
    if (ct && x.reasonFor(part, 'trends')) return gone(label, part);
    if (v !== null && v !== undefined) return f(label, x.pct(v / 100, 0, 0));
    if (ct && replicas === 0) return f(label, x.t('health.trends.notRunning'), true);
    return gone(label, part);
  };
  return [
    errorRate,
    avg,
    gauge(x.t('health.trends.cpu'), ct?.cpu_percent, `cpu:${k}`),
    gauge(x.t('health.trends.memory'), ct?.memory_percent, `memory:${k}`),
  ];
};

/** Dependencies that have platform metrics. Meta WhatsApp has its own send counts. */
const TRENDED_DEPENDENCIES = ['postgres', 'redis', 'model_provider'];

const MetaSendsLine = ({ item, view }: { item: HealthItem; view: TrendsView }) => {
  const x = useTrendsText(view);
  const m: MetaSends | null | undefined = view.data?.meta_sends;
  const scope = x.t('health.trends.meta.scope');
  if (!m)
    return (
      <p data-testid="meta-sends" className="m-0 text-meta text-ink-3">
        {scope}: {x.absent('meta_sends')}
      </p>
    );
  const noSends = m.sent_24h === 0 && m.failed_24h === 0 && m.error_rate_24h === null;
  const attempts = m.attempt_errors_by_status;
  return (
    <div data-testid="meta-sends" data-item={item.key} className="text-meta text-ink-3">
      <p className="m-0">
        {scope}:{' '}
        {noSends
          ? x.t('health.trends.meta.noSends')
          : // Denominator is sent + failed (final outcomes), as the backend computes the rate.
            x.t('health.trends.meta.failedShare', {
              count: m.sent_24h + m.failed_24h,
              total: x.whole(m.sent_24h + m.failed_24h),
              rate:
                m.error_rate_24h === null
                  ? x.t('health.fields.notAvailable')
                  : x.pct(m.error_rate_24h, 1, 1),
            })}
      </p>
      {attempts.length > 0 ? (
        <p data-testid="meta-attempts" className="m-0">
          {x.t('health.trends.meta.attempts')}{' '}
          <span className="font-mono text-ink-1">
            {attempts
              .map((a) =>
                x.t(
                  a.status_code === 429
                    ? 'health.trends.meta.attemptRateLimit'
                    : 'health.trends.meta.attempt',
                  {
                    status: a.status_code ?? x.t('health.trends.meta.noStatus'),
                    n: x.whole(a.count),
                  },
                ),
              )
              .join(', ')}
          </span>
        </p>
      ) : null}
      {x.reasonFor('meta_sends') ? (
        <p className="m-0">
          {x.t('health.trends.unavailableWhy', { reason: x.reasonFor('meta_sends') })}
        </p>
      ) : null}
    </div>
  );
};

export const DependencyTrendLine = ({ item, view }: { item: HealthItem; view: TrendsView }) => {
  const x = useTrendsText(view);
  if (item.key === 'meta_whatsapp') return <MetaSendsLine item={item} view={view} />;
  if (!TRENDED_DEPENDENCIES.includes(item.key)) return null;
  const part = `dependency:${item.key}`;
  const dt = view.data?.dependencies[item.key];
  if (!dt)
    return (
      <p data-testid="dependency-trend" className="m-0 text-meta text-ink-3">
        {x.absent(part)}
      </p>
    );
  const dash = x.t('health.fields.notAvailable');
  const reason = x.reasonFor(part);
  return (
    <p data-testid="dependency-trend" className="m-0 text-meta text-ink-3">
      {x.t('health.trends.latency')}{' '}
      <span className="font-mono text-ink-1">
        {dt.avg_latency_ms === null ? dash : x.ms(dt.avg_latency_ms)}
      </span>
      {' · '}
      {x.t('health.trends.errors')}{' '}
      <span className="font-mono text-ink-1">
        {dt.error_rate === null ? dash : x.pct(dt.error_rate, 1, 1)}
      </span>
      {reason ? ` · ${x.t('health.trends.unavailableWhy', { reason })}` : ''}
    </p>
  );
};

const dayKey = (d: Date): string => new Intl.DateTimeFormat('en-CA').format(d);

export const TimelineCard = ({ view }: { view: TrendsView }) => {
  const x = useTrendsText(view);
  // The backend sends [] for a failed timeline and lists "timeline" (or "trends") as unavailable.
  // "timeline" can be listed as partial while entries are still sent: show them, with the reason.
  const listed = x.reasonFor('timeline', 'trends');
  const sent = view.data?.timeline ?? [];
  const noData = !view.data || view.data.status === 'warming' || (!!listed && sent.length === 0);
  const events = noData ? null : sent;
  const partialNote =
    events && listed ? x.t('health.trends.unavailableWhy', { reason: listed }) : null;
  // Seven days ending on the day the trends were built, so a stale value keeps its own frame.
  const anchor = view.data?.generated_at ? new Date(view.data.generated_at) : new Date();
  const days = Array.from({ length: TIMELINE_DAYS }, (_, i) => {
    const d = new Date(anchor);
    d.setDate(anchor.getDate() - (TIMELINE_DAYS - 1 - i));
    return d;
  });
  const byDay = new Map<string, TimelineEvent[]>();
  (events ?? []).forEach((e) => {
    const k = dayKey(new Date(e.at));
    byDay.set(k, [...(byDay.get(k) ?? []), e]);
  });
  const nowKey = dayKey(new Date());
  const dayLabel = (d: Date) =>
    dayKey(d) === nowKey
      ? x.t('health.trends.today')
      : new Intl.DateTimeFormat(x.lang, { weekday: 'short', day: 'numeric' }).format(d);

  return (
    <section
      aria-labelledby="health-timeline-heading"
      data-testid="timeline-card"
      className="rounded-xl bg-surface-1 p-4 ring-1 ring-inset ring-outline"
    >
      <h2 id="health-timeline-heading" className="mb-2 text-h2 text-ink-1">
        {x.t('health.trends.timelineTitle')}
      </h2>
      {events === null ? (
        <p data-testid="timeline-absent" className="m-0 text-meta text-ink-3">
          {x.absent('timeline')}
        </p>
      ) : (
        <>
          {events.length === 0 ? (
            <p className="m-0 mb-2 text-meta text-ink-3">{x.t('health.trends.timelineEmpty')}</p>
          ) : null}
          <ol
            data-testid="timeline"
            className="m-0 grid list-none grid-cols-1 gap-1 p-0 xl:grid-cols-7"
          >
            {days.map((d) => (
              <li
                key={dayKey(d)}
                className="min-w-0 border-l-2 border-outline py-0.5 pl-2 xl:border-l-0 xl:border-t-2 xl:pl-0 xl:pt-1.5"
              >
                <span className="text-meta text-ink-3">{dayLabel(d)}</span>
                <ul className="m-0 list-none p-0">
                  {(byDay.get(dayKey(d)) ?? []).map((e) => {
                    const incident = e.kind !== 'deploy';
                    return (
                      <li
                        key={`${e.at}-${e.component}-${e.kind}`}
                        data-kind={e.kind}
                        className={`mt-1 break-words rounded border-l-[3px] bg-surface-2 px-1.5 py-0.5 text-meta ${incident ? 'border-status-down text-status-down' : 'border-status-ok text-ink-2'}`}
                      >
                        {/* Backend text: incidents already begin with "Failed:". */}
                        {e.text}
                        {e.text.includes(e.component) ? '' : ` (${e.component})`}
                      </li>
                    );
                  })}
                </ul>
              </li>
            ))}
          </ol>
        </>
      )}
      {partialNote ? (
        <p data-testid="timeline-partial" className="m-0 mt-2 text-meta text-status-degraded">
          {partialNote}
        </p>
      ) : null}
      <StaleNote view={view} />
    </section>
  );
};

const daysBetween = (fromIso: string, toIso: string): number =>
  (Date.parse(toIso) - Date.parse(fromIso)) / 86_400_000;

export const CostCard = ({ view }: { view: TrendsView }) => {
  const x = useTrendsText(view);
  const cost = view.data?.cost ?? null;
  const dash = x.t('health.fields.notAvailable');
  const money = (v: number | null) =>
    v === null || !cost?.currency
      ? dash
      : new Intl.NumberFormat(x.lang, { style: 'currency', currency: cost.currency }).format(v);
  const asOfIso = cost?.data_as_of ? `${cost.data_as_of}T00:00:00Z` : null;
  const asOf = asOfIso
    ? new Intl.DateTimeFormat(x.lang, { dateStyle: 'medium', timeZone: 'UTC' }).format(
        new Date(asOfIso),
      )
    : null;
  const generated = view.data?.generated_at ?? null;
  const costStale =
    asOfIso && generated ? daysBetween(asOfIso, generated) > COST_STALE_AFTER_DAYS : false;
  // A failed refresh serves the last good figures AND lists cost: keep them, add the reason.
  const shown = cost && (cost.yesterday !== null || cost.month_to_date !== null);
  const costReason = x.reasonFor('cost', 'trends');
  return (
    <section
      aria-labelledby="health-cost-heading"
      data-testid="cost-card"
      className="rounded-xl bg-surface-1 p-4 ring-1 ring-inset ring-outline"
    >
      <h2 id="health-cost-heading" className="mb-2 text-h2 text-ink-1">
        {x.t('health.trends.costTitle')}
      </h2>
      {shown ? (
        <>
          <dl className="m-0 grid grid-cols-2 gap-2">
            <div className="rounded-md bg-surface-2 px-3 py-1.5">
              <dt className="text-xs uppercase text-ink-3">{x.t('health.trends.costYesterday')}</dt>
              <dd className="m-0 font-mono text-body text-ink-1">{money(cost.yesterday)}</dd>
            </div>
            <div className="rounded-md bg-surface-2 px-3 py-1.5">
              <dt className="text-xs uppercase text-ink-3">{x.t('health.trends.costMonth')}</dt>
              <dd className="m-0 font-mono text-body text-ink-1">{money(cost.month_to_date)}</dd>
            </div>
          </dl>
          <p data-testid="cost-as-of" className="m-0 mt-2 text-meta text-ink-3">
            {x.t('health.trends.costLag')}{' '}
            {asOf ? x.t('health.trends.costAsOf', { date: asOf }) : x.t('health.trends.costNoDate')}
          </p>
          {costStale && asOf ? (
            <p data-testid="cost-stale" className="m-0 text-meta text-status-degraded">
              {x.t('health.trends.staleSince', { when: asOf })}
            </p>
          ) : null}
          {costReason ? (
            <p data-testid="cost-partial" className="m-0 text-meta text-status-degraded">
              {x.t('health.trends.unavailableWhy', { reason: costReason })}
            </p>
          ) : null}
          <StaleNote view={view} />
        </>
      ) : (
        <p data-testid="cost-absent" className="m-0 text-meta text-ink-3">
          {x.absent('cost')}
        </p>
      )}
    </section>
  );
};
